import * as React from 'react';
import { filter } from 'lodash';

import { createApplication, WorkstreamTrafficControl, entityConfigs, graphql, gql, EntityConfigSet } from '@shapeable/core';
import { ApplicationLogo, ApplicationLogoInverted, CourseCard } from './components';
import { themes } from '@shapeable/theme';
import { CourseLevel, LessonLevel, ExamAttempt } from './config';
import { installBilibiliVideoUrlBlur } from './helpers/video-url-blur';

const { Location, ImpactStory, MaturityStage, Course, FileAsset, Lesson, 
  Slice, Block, Video, Program, FeedEntry, View, Post,
  ExamQuestion, ExamQuestionOption,
} = entityConfigs;

const FS_ZH = { name: 'zh', label: '中文' };
// const FS_VI = { name: 'vi', label: 'Tiếng Việt' };
const LANG_FIELD_SETS = [FS_ZH /*, FS_VI */];

Slice.extraFieldSets = LANG_FIELD_SETS;
Program.extraFieldSets = LANG_FIELD_SETS;
Course.extraFieldSets = LANG_FIELD_SETS;
Lesson.extraFieldSets = LANG_FIELD_SETS;
Block.extraFieldSets = LANG_FIELD_SETS;
Video.extraFieldSets = LANG_FIELD_SETS;
FileAsset.extraFieldSets = LANG_FIELD_SETS;
View.extraFieldSets = LANG_FIELD_SETS;
ExamQuestion.extraFieldSets = LANG_FIELD_SETS;
ExamQuestionOption.extraFieldSets = LANG_FIELD_SETS;


if (Block) {
  Block.extraFields = {

    // language-specific fields
    title_zh: { type: 'text', fieldSet: 'zh', label: 'Title', badge: "中文" },
    // title_vi: { type: 'text', fieldSet: 'vi', label: 'Title', badge: "Tiếng Việt" },
  };
}

if (ExamQuestion) {
  ExamQuestion.extraFields = {

    // language-specific fields
    name_zh: { type: 'text', fieldSet: 'zh', label: 'Name', badge: "中文" },
    questionText_zh: { type: 'longText', fieldSet: 'zh', label: 'Question Text', badge: "中文" },

    // name_vi: { type: 'text', fieldSet: 'vi', label: 'Name', badge: "Tiếng Việt" },
    // questionText_vi: { type: 'longText', fieldSet: 'vi', label: 'Question Text', badge: "Tiếng Việt" },
  };
}

// course-registration/certificates/test-course-2-0/kiymet-ozgur-test-course-2-0-aa13bcea

if (ExamQuestionOption) {
  ExamQuestionOption.extraFields = {

    // language-specific fields
    name_zh: { type: 'text', fieldSet: 'zh', label: 'Name', badge: "中文" },
    label_zh: { type: 'text', fieldSet: 'zh', label: 'Label', badge: "中文" },
    valueKey_zh: { type: 'text', fieldSet: 'zh', label: 'Value Key', badge: "中文" },

    // name_vi: { type: 'text', fieldSet: 'vi', label: 'Name', badge: "Tiếng Việt" },
    // label_vi: { type: 'text', fieldSet: 'vi', label: 'Label', badge: "Tiếng Việt" },
    // valueKey_vi: { type: 'text', fieldSet: 'vi', label: 'Value Key', badge: "Tiếng Việt" },
  };
}

if (FeedEntry) {
  FeedEntry.extraFields = {
    // language-specific fields
    title_zh: { type: 'text', fieldSet: 'zh', label: 'Title', badge: "中文" },
    // title_vi: { type: 'text', fieldSet: 'vi', label: 'Title', badge: "Tiếng Việt" },
  };
}

if (Location) {
  Location.options = { statistics: 'basic' };
}

if (ImpactStory) {
  ImpactStory.label = 'Solution';
  ImpactStory.slug = 'solution';
}

if (MaturityStage) {
  MaturityStage.label = 'Pattern Type';
  MaturityStage.slug = 'pattern-type';
}

if (Course) {
  
  Course.extraFields = {
    accreditors: { type: 'linkedMultiple', entityTypeName: 'Organisation', label: 'Accreditors', description: 'Which organisations are accrediting this Course?' },
    courseLevel: { type: 'linked', entityTypeName: 'CourseLevel' },
    outlineNumber: { type: 'text', fieldSet: 'overview', maxWidth: '200px' },
    files: { type: 'linkedMultiple', entityTypeName: 'FileAsset', fieldSet: 'library' },

    feedEntries: { type: 'linkedMultiple', entityTypeName: 'FeedEntry', label: 'Articles', fieldSet: 'library' },
    posts: { type: 'linkedMultiple', entityTypeName: 'Post', label: 'Posts', fieldSet: 'library' },
    citations: { type: 'linkedMultiple', entityTypeName: 'Citation', label: 'Citations', fieldSet: 'library' },

    // language-specific fields
    name_zh: { type: 'text', fieldSet: 'zh', label: 'Name', badge: "中文" },
    description_zh: { type: 'longText', fieldSet: 'zh', label: 'Description', badge: "中文" },

    // name_vi: { type: 'text', fieldSet: 'vi', label: 'Name', badge: "Tiếng Việt" },
    // description_vi: { type: 'longText', fieldSet: 'vi', label: 'Description', badge: "Tiếng Việt" },
  }

  Course.list!.baseState = { orderBy: { fieldName: 'outlineNumberInt', desc: false } };

  Course.components = {
    card: CourseCard,
  }

  Course.list!.queryFields = graphql`
    banner { id name slug title description { text } alternateText openGraph { id image { id url } } }
    participants { ...EntityValueFields }
    outlineNumber
    presenters { ...EntityValueFields }
    providers { ...EntityValueFields }
    registrationUrl
    isCurrentUserParticipating
    description { plainTruncated: plain(truncate: { words: 50 }) plain ...ContentNodeFields }
    lessons { id }
    program { id name slug }
    startDate
  `

  // removed fileAssets field from course form as the field name needs to match slice field 'files'
  if (Course?.form?.fields?.fileAssets) {
    delete Course.form.fields.fileAssets;
  }
}

if (Lesson) {

  Lesson.form!.fieldSets = [
    ...(Lesson.form!.fieldSets || []),
    { name: 'exam', label: 'Exam Questions' },
  ];

  Lesson.extraFields = {
    lessonLevel: { type: 'linked', entityTypeName: 'LessonLevel' },
    outlineNumber: { type: 'text', fieldSet: 'overview', maxWidth: '200px' },
    files: { type: 'linkedMultiple', entityTypeName: 'FileAsset', fieldSet: 'library' },
    feedEntries: { type: 'linkedMultiple', entityTypeName: 'FeedEntry', label: 'Articles', fieldSet: 'library' },
    posts: { type: 'linkedMultiple', entityTypeName: 'Post', label: 'Posts', fieldSet: 'library' },

    // language-specific fields
    name_zh: { type: 'text', fieldSet: 'zh', label: 'Name', badge: "中文" },
    description_zh: { type: 'longText', fieldSet: 'zh', label: 'Description', badge: "中文" },

    // name_vi: { type: 'text', fieldSet: 'vi', label: 'Name', badge: "Tiếng Việt" },
    // description_vi: { type: 'longText', fieldSet: 'vi', label: 'Description', badge: "Tiếng Việt" },
  }

  if (Lesson.form!.fields?.fileAssets) {
    delete Lesson.form!.fields.fileAssets;
  }
}

if (FileAsset) {
  // FileAsset.form.fieldSets = [
  //   ...filter(FileAsset.form.fieldSets, (fs) => !includes(['overview', 'library',], fs.name)),
  //   { name: 'overview' },
  //   { name: 'library' },
  // ];
  // FileAsset.extraFields = {
  //   organisations: { type: 'linkedMultiple', entityTypeName: 'Organisation', fieldSet: 'library' },
  //   countries: { type: 'linkedMultiple', entityTypeName: 'Economy', fieldSet: 'library' }
  // };

  // language-specific fields
  FileAsset.extraFields = {
    name_zh: { type: 'text', fieldSet: 'zh', label: 'Name', badge: "中文" },
    // name_vi: { type: 'text', fieldSet: 'vi', label: 'Name', badge: "Tiếng Việt" },
  };
}

if (Post) {
  Post.extraFieldSets = [
    { name: 'places', label: 'Places' },
  ]
  Post.extraFields = {
    economies: { type: 'linkedMultiple', entityTypeName: 'Economy', fieldSet: 'places' },
  }
}

if (Program) {
   Program.extraFields = {
    // language-specific fields
    name_zh: { type: 'text', fieldSet: 'zh', label: 'Name', badge: "中文" },
    // name_vi: { type: 'text', fieldSet: 'vi', label: 'Name', badge: "Tiếng Việt" },

   }
}

if (Slice) {
  Slice.extraFields = {
    // mandarin fields for slice 
    title_zh: { type: 'text', fieldSet: 'zh', label: 'Title', badge: "中文" },
    subtitle_zh: { type: 'text', fieldSet: 'zh', label: 'Subtitle', badge: "中文" },
    label_zh: { type: 'text', fieldSet: 'zh', label: 'Label', badge: "中文" },
    intro_zh: { type: 'longText', fieldSet: 'zh', label: 'Intro', badge: "中文" },
    content_zh: { type: 'longText', fieldSet: 'zh', label: 'Content', badge: "中文" },

  }
}

if (View) {
  View.extraFields = {
    // language-specific fields
    label_zh: { type: 'text', fieldSet: 'zh', label: 'Label', badge: "中文" },
    // label_vi: { type: 'text', fieldSet: 'vi', label: 'Label', badge: "Tiếng Việt" },
  };
}

if (Video) {
  Video.extraFields = {
    // language-specific fields
    name_zh: { type: 'text', fieldSet: 'zh', label: 'Name', badge: "中文" },
    // name_vi: { type: 'text', fieldSet: 'vi', label: 'Name', badge: "Tiếng Việt" },
  };

  // Install bilibili url blur handler to auto-populate video details when a bilibili url is entered.
  // We have patched the onBlur url field handler in the video-config file so if url is a youtube url, 
  // we fallback to calling the existing youtube handler.
  installBilibiliVideoUrlBlur(Video);
}

const myEntityConfigs = { 
  ...entityConfigs,
  CourseLevel,
  LessonLevel,
  ExamAttempt,
};

export const application = createApplication({
  theme: themes.JUSTICE_THEME,
  entityConfigs: myEntityConfigs as EntityConfigSet,
  options: {
    systemsMapping: 'full',
  },
  components: {
    Logo: ApplicationLogo,
    LogoInverted: ApplicationLogoInverted,
  },
});

/* ---- Patch Entities definitions afterwards to ensure sub-props are already set --- */

const entities = application.entities;
const entitiesToRemove = entities as EntityConfigSet;
// -- ensure that traffic control team is not shown in lists
entities.Team.list.filterItems = (items) => filter(items, item => item.slug !== 'traffic-control');

delete entitiesToRemove.Trend;
delete entitiesToRemove.Topic;
delete entitiesToRemove.SubTopic;
// delete entities.Program;
// delete entities.Course;

// -- show Traffic controllers
entities.Team.list.postBanner = <WorkstreamTrafficControl />;